import React, {useEffect, useMemo, useState} from 'react';
import {ReactComponent as CalendarIcon} from '../../../assets/images/svg/CalendarIconSea.svg';
import {ReactComponent as ClockIcon} from '../../../assets/images/svg/ClockIconSea.svg';
import {ReactComponent as KeyIcon} from '../../../assets/images/svg/Key.svg';
import {ReactComponent as PinIcon} from '../../../assets/images/svg/PinSea.svg';
import {ReactComponent as AttentionIcon} from '../../../assets/images/svg/RedAttintionOutlined.svg';
import {ReactComponent as CashIcon} from '../../../assets/images/svg/CardsIcon.svg';
import {ReactComponent as RoomIcon} from '../../../assets/images/svg/RoomSeaIcon.svg';
import {ReactComponent as SeaStar} from '../../../assets/images/svg/EmptySeaStarIcon.svg';
import axios from "axios";
import UniversalPopup from "../Popup/Popup.component";
import BookingWidgetTestComponent from "../BookingWidget/BookingWidgetTest.component";
import BookingWidgetTestComponentMobile from "../../mobile/RoomDetailsPage/BookingWidgetTest.component";
import {useSelector} from "react-redux";
import LoaderComponent from "../../mobile/Loader/Loader.component";
import styles from './ActiveBooking.module.scss'
import {RootState} from "../../../reducers/types";
import {
    formatCyprusDDMMYYYY,
    getCyprusDayName,
    isMoreThanHoursBeforeCyprusStart,
} from "../../../utils/cyprusTime";

interface ActiveBookingPropsInterface {
    date: string;
    slot: any;
    address: string;
    keyCode: string;
    withPrice?: boolean;
    withDeletion?: boolean;
    readonly?: boolean;
    amount?: number | null;
    id?: string | null;
    handleOrdersChange? : any;
    width?: number;
    canceled?: boolean;
    title: string;
    order?: any;
}

const ActiveBookingComponent:React.FC <ActiveBookingPropsInterface> = ({date, slot, address, keyCode, withPrice= false, amount = null, withDeletion = false, id= null, handleOrdersChange = ()=>{}, width = 24, canceled = false, title, readonly= false, order=null}) => {
    const [popup, setPopup] = useState<any>(null)
    const [loading, setLoading] = useState<any>(false)
    const [editBookingDialog, setEditBookingDialog] = useState<boolean>(false)
    const building = useSelector((state: RootState)=>state.buildings.buildings)
    const [currentRoom, setCurrentRoom] = useState<any>(null)
    const [isDesktop, setIsDesktop] = useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth > 500 : true
    )

    function formatTimeTo12HourFormat(hour: any) {
        const amOrPm = hour >= 12 ? 'pm' : 'am';
        const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
        const formattedHourWithLeadingZero = formattedHour.toString().padStart(2, '0');


        return `${formattedHourWithLeadingZero} ${amOrPm}`;
    }

    function formatCurrentDate(date: Date) {
        const currentDate = date;
        const day = String(currentDate.getDate()).padStart(2, '0');
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const year = currentDate.getFullYear();
        return `${day}.${month}.${year}`;
    }
    function updateOrder(data:any, amount:any) {
        if (!order || !order.roomID) {
            setLoading(false)
            return
        }

        setLoading(true)
        
        // Формируем payload для проверки слотов
        const payload = {
            roomID: order.roomID._id,
            buildingID: order.buildingID?._id || order.buildingID,
            date: new Date(),
            slots: data,
            userID: order.userID?._id || order.userID,
            excludeOrderId: id,
        }

        // Проверяем доступность слотов перед обновлением
        axios.post('https://gscoworking.com/api/orders/check-slots', payload)
            .then((answer) => {
                // Проверяем доступность слотов
                if (answer.data === true) {
                    // Слот доступен - обновляем заказ
                    let updateData = {slots: data, status: 'Edited'}
                    axios.put(`https://gscoworking.com/api/orders/${id}`, updateData)
                        .then((answer) => {
                            setEditBookingDialog(false)
                            handleOrdersChange()
                            setLoading(false)
                        })
                        .catch((error) => {
                            console.error('Error updating order:', error)
                            setPopup({
                                title: 'Error',
                                text: 'Failed to update booking. Please try again.',
                                buttonText2: 'Ok',
                                onButton2Click: () => setPopup(null)
                            })
                            setLoading(false)
                        })
                } else {
                    // Слот занят - показываем ошибку
                    setPopup({
                        title: 'Slot unavailable',
                        text: 'This time slot is no longer available. Please select another time.',
                        buttonText2: 'Ok',
                        onButton2Click: () => setPopup(null)
                    })
                    setLoading(false)
                }
            })
            .catch((error) => {
                // Обработка ошибки сети или сервера
                console.error('Error checking slots:', error)
                setPopup({
                    title: 'Error',
                    text: 'An error occurred while checking availability. Please try again.',
                    buttonText2: 'Ok',
                    onButton2Click: () => setPopup(null)
                })
                setLoading(false)
            })
    }

    function openReschedule(e?: React.MouseEvent) {
        e?.preventDefault()
        e?.stopPropagation()
        setEditBookingDialog(true)
    }

    function closeReschedule() {
        setEditBookingDialog(false)
    }

    // Reschedule allowed only if booking start (Cyprus / Asia/Nicosia) is > 24h away.
    // Studio hours in the app are always Cyprus time — do not use browser local TZ.
    const canReschedule = useMemo(() => {
        if (!withDeletion || canceled || readonly) return false
        if (!slot || !slot[0] || slot[0].date == null || slot[0].start == null) return false
        return isMoreThanHoursBeforeCyprusStart(slot[0].date, Number(slot[0].start), 24)
    }, [withDeletion, canceled, readonly, slot])

    const displayDate = useMemo(() => {
        if (slot?.[0]?.date != null) {
            const cyprus = formatCyprusDDMMYYYY(slot[0].date)
            if (cyprus) return cyprus
        }
        return date
    }, [slot, date])

    const dayName = useMemo(() => {
        if (slot?.[0]?.date != null) {
            return getCyprusDayName(slot[0].date)
        }
        return ''
    }, [slot])

    useEffect(() => {
        const onResize = () => setIsDesktop(window.innerWidth > 500)
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    useEffect(() => {
       if (!order || !order.roomID) return
       const rooms = building && building[0] && Array.isArray(building[0].rooms)
           ? building[0].rooms
           : []
       const fromAllBuildings = (building || []).flatMap((b: any) => b.rooms || [])
       const roomList = rooms.length ? rooms : fromAllBuildings
       const match = roomList.find((x: any) => x && order.roomID && (x._id === order.roomID._id || x._id === order.roomID))
       if (match) {
           setCurrentRoom(match)
       } else if (order.roomID && typeof order.roomID === 'object') {
           // Fallback so Reschedule still opens even if buildings cache is empty
           setCurrentRoom(order.roomID)
       }
    }, [order, building]);

    const slotDuration = slot?.[0]
        ? (slot[0].end === 0 ? 24 : slot[0].end) - slot[0].start
        : 1

    // Stable reference: a fresh array each render made the widget reset the user's pick
    const preselectedSlots = useMemo(() => (slot?.[0] ? [slot[0]] : []), [slot])

    const roomForWidget = currentRoom && currentRoom._id
        ? {
            ...currentRoom,
            price: '0',
            oldPrice: '0',
            minHours: slotDuration,
          }
        : null

    return (
        <div className={`row gap-20 ${styles.bookingRoot}`}>
            <div className={`col gap-30 col-${width}`} style={readonly ? {opacity: 0.5} : {opacity: 1}}>
                <div className={styles.headerRow}>
                    <div className={styles.headerLeft}>
                        <CalendarIcon/>
                        <span>{displayDate}</span>
                        {dayName ? <span className={styles.dayName}>{dayName}</span> : null}
                    </div>
                    {canReschedule && (
                        <button
                            type="button"
                            className={`magenta-text underline pointer ${styles.rescheduleBtn}`}
                            onClick={openReschedule}
                        >
                            Reschedule
                        </button>
                    )}
                    {readonly && (
                        <div className={'row align-items-center pointer'}>
                            <SeaStar/>
                            <SeaStar/>
                            <SeaStar/>
                            <SeaStar/>
                            <SeaStar/>
                        </div>
                    )}
                </div>

                <div className={'row align-items-center gap-18'}>
                    <RoomIcon />
                    <span>{title}</span>
                </div>
                <div className={'row align-items-center gap-18'}>
                    <ClockIcon />
                    <span>{`${formatTimeTo12HourFormat(slot[0].start)} - ${formatTimeTo12HourFormat(slot[0].end)}`}</span>
                </div>
                <div className={'row align-items-center gap-18'}>
                    <PinIcon />
                    <span className={'review-address'}>{address}</span>
                </div>
                {!readonly && (
                    <div className={'row align-items-center gap-18'}>
                        <KeyIcon />
                        <span>*{keyCode}#</span>
                    </div>
                )}
                {withPrice && (
                    <div className={'row align-items-center gap-18'}>
                        <CashIcon />
                        <span>{typeof amount === 'number' ? amount.toFixed(2) : amount} €</span>
                    </div>
                )}
                {!readonly && (
                    <div className={'row align-items-center gap-18'}>
                        <AttentionIcon />
                        <span className={'text-12'}>You have the option to make changes to your booking up to 24 hours before the scheduled date and time

</span>
                    </div>
                )}
                {popup && (
                    <UniversalPopup // This should be the Popup component from usePopup
                        title={popup.title}
                        text={popup.text}
                        onClose={()=>setPopup(null)}
                        buttonText1={popup.buttonText1}
                        buttonText2={popup.buttonText2}
                        onButton1Click={popup.onButton1Click}
                        onButton2Click={popup.onButton2Click}
                    />
                )}
            </div>
            {editBookingDialog && order && order.roomID && (
                <div className={`col gap-30 col-${23-width} ${styles.editForm}`}>
                    <div className={styles.editFormHeader}>
                        <span className={'text-14 magenta-text underline pointer'} onClick={closeReschedule}>
                            Close
                        </span>
                    </div>
                    {!roomForWidget && (
                        <span className={'text-14'}>Loading studio…</span>
                    )}
                    {roomForWidget && isDesktop && (
                        <BookingWidgetTestComponent
                            date={displayDate || formatCurrentDate(new Date(slot[0].date))}
                            hours={slotDuration}
                            callback={updateOrder}
                            room={roomForWidget}
                            buttonText={'Change'}
                            multiple={false}
                            mode={'edit'}
                            preselected={preselectedSlots}
                        />
                    )}
                    {roomForWidget && !isDesktop && (
                        <BookingWidgetTestComponentMobile
                            date={displayDate || formatCurrentDate(new Date(slot[0].date))}
                            hours={slotDuration}
                            callback={updateOrder}
                            room={roomForWidget}
                            buttonText={'Change'}
                            multiple={false}
                            mode={'edit'}
                            preselected={preselectedSlots}
                        />
                    )}
                </div>
            )}
            {loading && (
                <LoaderComponent text={'Please wait. Your booking is processing'} loading={loading} >
                </LoaderComponent>
            )}
        </div>
    );
};

export default ActiveBookingComponent;
export {};
